import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import {
  AgentDto,
  ContactActivityTypes,
  CreateActivityType,
  ProspectActivity,
  ProspectDetails,
  ProspectDto
} from 'service/dto/prospect.dto';

export const useGetProspects = () => {
  return useQuery<ProspectDto, AxiosError<Error>>(
    ['get_prospect'],
    () => services.manageProspectService.getProspects(),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false
    }
  );
};

export const useGetProspectDetails = ({
  prospectId
}: {
  prospectId: string;
}) => {
  return useQuery<ProspectDetails, AxiosError<Error>>(
    ['get_prospect_details'],
    () => services.manageProspectService.getProspectDetails(prospectId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false
    }
  );
};

export const useGetProspectActivities = ({
  prospectId
}: {
  prospectId: string;
}) => {
  return useQuery<ProspectActivity, AxiosError<Error>>(
    ['get_prospect_activities'],
    () => services.manageProspectService.getProspectActivity(prospectId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false
    }
  );
};

export const useAssignAgentToProspect = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      prospectId,
      agentId
    }: {
      prospectId: string;
      agentId: number;
    }): Promise<any> => {
      return services.manageProspectService.assignAgentToProspect(
        prospectId,
        agentId
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-staff']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetAgents = () => {
  return useQuery<AgentDto, AxiosError<Error>>(
    ['get_agents'],
    () => services.manageProspectService.getAgentList(),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false
    }
  );
};

export const useCreateActivity = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (createActivityType: CreateActivityType): Promise<any> => {
      return services.manageProspectService.createActivity(createActivityType);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-activity']);
        queryClient.invalidateQueries(['get_prospect_activities']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetAgentActivityType = () => {
  return useQuery<ContactActivityTypes, AxiosError<Error>>(
    ['get_agent_activity_type'],
    () => services.manageProspectService.getAgentActivityType(),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false
    }
  );
};

export const useGetAgentContactType = () => {
  return useQuery<ContactActivityTypes, AxiosError<Error>>(
    ['get_agent_contact_type'],
    () => services.manageProspectService.getAgentContactType(),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false
    }
  );
};
